// Text catalog for the ESG page. Icons and images live in
// src/pages/EsgPage.tsx and are aligned by index with the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Card = { title: string; text: string };
type Sdg = { num: number; title: string; text: string };

type EsgStrings = {
  meta: PageMeta;
  hero: { badge: string; title: string; p1: string; iso: string; pAfter: string };
  iso: {
    badge: string;
    heading: string;
    p1: string;
    p2: string;
    p3: string;
    cta: string;
  };
  pillars: { heading: string; intro: string; cards: Card[] };
  initiatives: { heading: string; intro: string; cards: Card[] };
  sdgsSection: { badge: string; heading: string; intro: string };
  sdgs: Sdg[];
  cta: { heading: string; body: string; cta: string };
};

export const esgStrings: Record<Locale, EsgStrings> = {
  da: {
    meta: {
      title: "ESG - Bæredygtighed og ansvarlighed - MI Support",
      description:
        "MI Supports arbejde med ESG: miljø, sociale forhold og god ledelse. ISO 14001-certificeret og engageret i FN's Verdensmål.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-4_1400x1400.jpg",
    },
    hero: {
      badge: "Om os · ESG",
      title: "ESG i MI Support",
      p1: "Ansvarlighed er en integreret del af måden, vi driver virksomhed på. Vi arbejder struktureret med miljø, sociale forhold og god ledelse, fordi det skaber værdi for vores kunder, vores medarbejdere og det samfund, vi er en del af.",
      iso: "ISO 14001-certificeret",
      pAfter: " og lader FN's Verdensmål være pejlemærker for vores arbejde.",
    },
    iso: {
      badge: "ISO 14001-certificeret",
      heading: "Miljøledelse i system",
      p1: "Siden starten af 2023 har MI Support været ISO 14001-certificeret. Det er den internationalt anerkendte standard for miljøledelsessystemer, og den forpligter os til at arbejde struktureret og dokumenteret med vores miljøpåvirkning.",
      p2: "Certificeringen betyder, at vi løbende identificerer, måler og forbedrer de miljøforhold, vi har indflydelse på, fra vores egen drift til de råd, vi giver vores kunder. Den giver vores samarbejdspartnere sikkerhed for, at bæredygtighed ikke blot er en ambition, men en del af vores daglige ledelsessystem.",
      p3: "Når I vælger MI Support, vælger I en rådgiver, der har miljøansvar som en integreret del af virksomhedskulturen.",
      cta: "Kontakt os om ESG",
    },
    pillars: {
      heading: "De tre ESG-søjler",
      intro:
        "Vi arbejder ud fra de tre klassiske ESG-søjler, Environmental, Social og Governance, og lader dem styre, hvordan vi driver virksomhed og rådgiver vores kunder.",
      cards: [
        {
          title: "Environmental",
          text: "Vi minimerer vores miljøpåvirkning gennem ISO 14001-certificeret miljøledelse, bevidste valg i hverdagen og rådgivning, der hjælper vores kunder med at bygge og drifte mere bæredygtigt.",
        },
        {
          title: "Social",
          text: "Vi prioriterer trivsel, sikkerhed og diversitet, både hos vores egne medarbejdere og på de byggepladser, vi er en del af. Mennesker er vores vigtigste ressource.",
        },
        {
          title: "Governance",
          text: "God ledelse, gennemsigtighed og høj forretningsetik er grundlaget for vores samarbejder. Vi arbejder struktureret med compliance, kvalitet og ansvarlig forretningsførelse.",
        },
      ],
    },
    initiatives: {
      heading: "Vores ESG-initiativer",
      intro:
        "Konkret arbejder vi med en række initiativer, der gør ESG til praksis, ikke blot politik.",
      cards: [
        {
          title: "ISO 14001-certificeret miljøledelse",
          text: "Siden 2023 har vi været ISO 14001-certificeret. Det forpligter os til løbende at måle, vurdere og forbedre vores miljøpåvirkning, og til at integrere miljøhensyn i alle dele af vores forretning.",
        },
        {
          title: "Bæredygtig bygherrerådgivning",
          text: "Vi rådgiver bygherrer om DGNB, energieffektivitet, materialevalg og livscyklusbetragtninger, så bæredygtighed bliver en integreret del af projektets økonomi og tidsplan.",
        },
        {
          title: "Trivsel, sikkerhed og diversitet",
          text: "Sikkerhed på pladsen, et sundt arbejdsmiljø og en inkluderende kultur er ikke til forhandling. Vi arbejder målrettet med HSE-ledelse og en mangfoldig medarbejdersammensætning.",
        },
        {
          title: "Ansvarlig leverandørkæde",
          text: "Vi stiller krav til vores samarbejdspartnere om ordentlige arbejdsforhold, miljøhensyn og forretningsetik, og vi vælger leverandører, der deler vores værdier.",
        },
      ],
    },
    sdgsSection: {
      badge: "FN's Verdensmål",
      heading: "Vi bidrager til FN's Verdensmål",
      intro:
        "FN's 17 Verdensmål for bæredygtig udvikling er en fælles ramme for, hvordan vi som samfund og virksomheder kan bidrage til en mere bæredygtig fremtid. Vi har valgt at fokusere særligt på de mål, hvor vi kan gøre den største forskel gennem vores arbejde.",
    },
    sdgs: [
      { num: 3, title: "Sundhed og trivsel", text: "Vi sikrer et sundt og sikkert arbejdsmiljø gennem struktureret HSE-ledelse og fokus på medarbejdertrivsel." },
      { num: 5, title: "Ligestilling mellem kønnene", text: "Vi arbejder aktivt for ligestilling og lige muligheder i rekruttering, udvikling og ledelse." },
      { num: 8, title: "Anstændige jobs og økonomisk vækst", text: "Vi skaber ordentlige arbejdsforhold og udviklingsmuligheder for vores medarbejdere, og stiller samme krav til vores partnere." },
      { num: 9, title: "Industri, innovation og infrastruktur", text: "Vi bidrager til at realisere komplekse industri-, energi- og infrastrukturprojekter med fokus på kvalitet og ansvarlighed." },
      { num: 11, title: "Bæredygtige byer og lokalsamfund", text: "Vi rådgiver om byggerier, der er holdbare, energieffektive og tilpasset fremtidens behov." },
      { num: 12, title: "Ansvarligt forbrug og produktion", text: "Vi arbejder med ressourceeffektivitet, affaldsminimering og bevidste materialevalg i de projekter, vi er en del af." },
      { num: 13, title: "Klimaindsats", text: "Gennem ISO 14001 og rådgivning om CO₂-reduktion bidrager vi til at sænke klimapåvirkningen fra byggeri og drift." },
      { num: 17, title: "Partnerskaber for handling", text: "Bæredygtighed skabes i fællesskab. Vi indgår i tætte partnerskaber med kunder, rådgivere og entreprenører." },
    ],
    cta: {
      heading: "Vil I høre mere om vores ESG-arbejde?",
      body: "Vi deler gerne vores erfaringer med ISO 14001, bæredygtig bygherrerådgivning og hvordan vi konkret arbejder med FN's Verdensmål i vores projekter.",
      cta: "Kontakt os",
    },
  },

  en: {
    meta: {
      title: "ESG - Sustainability and responsibility - MI Support",
      description:
        "MI Support's work with ESG: environment, social conditions and good governance. ISO 14001-certified and committed to the UN Sustainable Development Goals.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-4_1400x1400.jpg",
    },
    hero: {
      badge: "About us · ESG",
      title: "ESG at MI Support",
      p1: "Responsibility is an integrated part of the way we run our business. We work in a structured way with environment, social conditions and good governance, because it creates value for our customers, our employees and the society we are part of.",
      iso: "ISO 14001-certified",
      pAfter: " and let the UN Sustainable Development Goals guide our work.",
    },
    iso: {
      badge: "ISO 14001-certified",
      heading: "Environmental management in a system",
      p1: "Since the start of 2023, MI Support has been ISO 14001-certified. It is the internationally recognised standard for environmental management systems, and it commits us to work in a structured and documented way with our environmental impact.",
      p2: "The certification means that we continuously identify, measure and improve the environmental conditions we can influence, from our own operations to the advice we give our customers. It gives our partners the assurance that sustainability is not just an ambition, but part of our daily management system.",
      p3: "When you choose MI Support, you choose an advisor who has environmental responsibility as an integrated part of the corporate culture.",
      cta: "Contact us about ESG",
    },
    pillars: {
      heading: "The three ESG pillars",
      intro:
        "We work from the three classic ESG pillars, Environmental, Social and Governance, and let them guide how we run our business and advise our customers.",
      cards: [
        {
          title: "Environmental",
          text: "We minimise our environmental impact through ISO 14001-certified environmental management, conscious everyday choices and advice that helps our customers build and operate more sustainably.",
        },
        {
          title: "Social",
          text: "We prioritise well-being, safety and diversity, both among our own employees and on the construction sites we are part of. People are our most important resource.",
        },
        {
          title: "Governance",
          text: "Good management, transparency and high business ethics are the foundation of our partnerships. We work in a structured way with compliance, quality and responsible business conduct.",
        },
      ],
    },
    initiatives: {
      heading: "Our ESG initiatives",
      intro:
        "In concrete terms, we work with a range of initiatives that make ESG a practice, not just a policy.",
      cards: [
        {
          title: "ISO 14001-certified environmental management",
          text: "Since 2023 we have been ISO 14001-certified. It commits us to continuously measure, assess and improve our environmental impact, and to integrate environmental considerations into all parts of our business.",
        },
        {
          title: "Sustainable client consultancy",
          text: "We advise clients on DGNB, energy efficiency, choice of materials and life-cycle considerations, so that sustainability becomes an integrated part of the project's economy and schedule.",
        },
        {
          title: "Well-being, safety and diversity",
          text: "Safety on site, a healthy working environment and an inclusive culture are non-negotiable. We work purposefully with HSE management and a diverse workforce composition.",
        },
        {
          title: "Responsible supply chain",
          text: "We require our partners to maintain proper working conditions, environmental considerations and business ethics, and we choose suppliers who share our values.",
        },
      ],
    },
    sdgsSection: {
      badge: "UN Sustainable Development Goals",
      heading: "We contribute to the UN Sustainable Development Goals",
      intro:
        "The UN's 17 Sustainable Development Goals are a shared framework for how we, as a society and as companies, can contribute to a more sustainable future. We have chosen to focus particularly on the goals where we can make the biggest difference through our work.",
    },
    sdgs: [
      { num: 3, title: "Good health and well-being", text: "We ensure a healthy and safe working environment through structured HSE management and a focus on employee well-being." },
      { num: 5, title: "Gender equality", text: "We work actively for equality and equal opportunities in recruitment, development and management." },
      { num: 8, title: "Decent work and economic growth", text: "We create proper working conditions and development opportunities for our employees, and place the same requirements on our partners." },
      { num: 9, title: "Industry, innovation and infrastructure", text: "We help realise complex industrial, energy and infrastructure projects with a focus on quality and responsibility." },
      { num: 11, title: "Sustainable cities and communities", text: "We advise on buildings that are durable, energy-efficient and adapted to the needs of the future." },
      { num: 12, title: "Responsible consumption and production", text: "We work with resource efficiency, waste minimisation and conscious choice of materials in the projects we are part of." },
      { num: 13, title: "Climate action", text: "Through ISO 14001 and advice on CO₂ reduction, we help lower the climate impact of construction and operation." },
      { num: 17, title: "Partnerships for the goals", text: "Sustainability is created together. We enter into close partnerships with customers, advisors and contractors." },
    ],
    cta: {
      heading: "Want to hear more about our ESG work?",
      body: "We are happy to share our experience with ISO 14001, sustainable client consultancy and how we work concretely with the UN Sustainable Development Goals in our projects.",
      cta: "Contact us",
    },
  },

  de: {
    meta: {
      title: "ESG - Nachhaltigkeit und Verantwortung - MI Support",
      description:
        "Die ESG-Arbeit von MI Support: Umwelt, Soziales und gute Unternehmensführung. ISO 14001-zertifiziert und den UN-Nachhaltigkeitszielen verpflichtet.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-4_1400x1400.jpg",
    },
    hero: {
      badge: "Über uns · ESG",
      title: "ESG bei MI Support",
      p1: "Verantwortung ist ein integraler Bestandteil unserer Geschäftstätigkeit. Wir arbeiten strukturiert an Umwelt, sozialen Belangen und guter Unternehmensführung, weil dies Wert für unsere Kunden, unsere Mitarbeiter und die Gesellschaft schafft, deren Teil wir sind.",
      iso: "ISO 14001-zertifiziert",
      pAfter: " und lassen uns von den UN-Nachhaltigkeitszielen bei unserer Arbeit leiten.",
    },
    iso: {
      badge: "ISO 14001-zertifiziert",
      heading: "Umweltmanagement mit System",
      p1: "Seit Anfang 2023 ist MI Support ISO 14001-zertifiziert. Es ist die international anerkannte Norm für Umweltmanagementsysteme und verpflichtet uns, strukturiert und dokumentiert an unserer Umweltwirkung zu arbeiten.",
      p2: "Die Zertifizierung bedeutet, dass wir laufend die Umweltaspekte, auf die wir Einfluss haben, identifizieren, messen und verbessern – von unserem eigenen Betrieb bis zu den Empfehlungen, die wir unseren Kunden geben. Sie gibt unseren Partnern die Sicherheit, dass Nachhaltigkeit nicht nur ein Anspruch ist, sondern Teil unseres täglichen Managementsystems.",
      p3: "Wenn Sie sich für MI Support entscheiden, wählen Sie einen Berater, für den Umweltverantwortung ein integraler Bestandteil der Unternehmenskultur ist.",
      cta: "Kontaktieren Sie uns zum Thema ESG",
    },
    pillars: {
      heading: "Die drei ESG-Säulen",
      intro:
        "Wir arbeiten nach den drei klassischen ESG-Säulen Environmental, Social und Governance und lassen sie bestimmen, wie wir unser Unternehmen führen und unsere Kunden beraten.",
      cards: [
        {
          title: "Environmental",
          text: "Wir minimieren unsere Umweltwirkung durch ISO 14001-zertifiziertes Umweltmanagement, bewusste Entscheidungen im Alltag und Beratung, die unseren Kunden hilft, nachhaltiger zu bauen und zu betreiben.",
        },
        {
          title: "Social",
          text: "Wir legen Wert auf Wohlbefinden, Sicherheit und Diversität – sowohl bei unseren eigenen Mitarbeitern als auch auf den Baustellen, deren Teil wir sind. Menschen sind unsere wichtigste Ressource.",
        },
        {
          title: "Governance",
          text: "Gute Führung, Transparenz und hohe Geschäftsethik sind die Grundlage unserer Zusammenarbeit. Wir arbeiten strukturiert an Compliance, Qualität und verantwortungsvoller Unternehmensführung.",
        },
      ],
    },
    initiatives: {
      heading: "Unsere ESG-Initiativen",
      intro:
        "Konkret arbeiten wir an einer Reihe von Initiativen, die ESG zur Praxis machen, nicht nur zur Richtlinie.",
      cards: [
        {
          title: "ISO 14001-zertifiziertes Umweltmanagement",
          text: "Seit 2023 sind wir ISO 14001-zertifiziert. Das verpflichtet uns, unsere Umweltwirkung laufend zu messen, zu bewerten und zu verbessern und Umweltaspekte in alle Teile unseres Geschäfts zu integrieren.",
        },
        {
          title: "Nachhaltige Bauherrenberatung",
          text: "Wir beraten Bauherren zu DGNB, Energieeffizienz, Materialwahl und Lebenszyklusbetrachtungen, sodass Nachhaltigkeit zu einem integralen Bestandteil der Wirtschaftlichkeit und des Zeitplans des Projekts wird.",
        },
        {
          title: "Wohlbefinden, Sicherheit und Diversität",
          text: "Sicherheit auf der Baustelle, ein gesundes Arbeitsumfeld und eine inklusive Kultur sind nicht verhandelbar. Wir arbeiten zielgerichtet an HSE-Management und einer vielfältigen Belegschaft.",
        },
        {
          title: "Verantwortungsvolle Lieferkette",
          text: "Wir verlangen von unseren Partnern ordentliche Arbeitsbedingungen, Umweltrücksicht und Geschäftsethik und wählen Lieferanten, die unsere Werte teilen.",
        },
      ],
    },
    sdgsSection: {
      badge: "UN-Nachhaltigkeitsziele",
      heading: "Wir tragen zu den UN-Nachhaltigkeitszielen bei",
      intro:
        "Die 17 UN-Nachhaltigkeitsziele sind ein gemeinsamer Rahmen dafür, wie wir als Gesellschaft und als Unternehmen zu einer nachhaltigeren Zukunft beitragen können. Wir haben uns entschieden, uns besonders auf die Ziele zu konzentrieren, bei denen wir durch unsere Arbeit den größten Unterschied machen können.",
    },
    sdgs: [
      { num: 3, title: "Gesundheit und Wohlergehen", text: "Wir sorgen für ein gesundes und sicheres Arbeitsumfeld durch strukturiertes HSE-Management und Fokus auf das Wohlbefinden der Mitarbeiter." },
      { num: 5, title: "Geschlechtergleichheit", text: "Wir setzen uns aktiv für Gleichstellung und Chancengleichheit bei Rekrutierung, Entwicklung und Führung ein." },
      { num: 8, title: "Menschenwürdige Arbeit und Wirtschaftswachstum", text: "Wir schaffen ordentliche Arbeitsbedingungen und Entwicklungsmöglichkeiten für unsere Mitarbeiter und stellen dieselben Anforderungen an unsere Partner." },
      { num: 9, title: "Industrie, Innovation und Infrastruktur", text: "Wir tragen zur Realisierung komplexer Industrie-, Energie- und Infrastrukturprojekte mit Fokus auf Qualität und Verantwortung bei." },
      { num: 11, title: "Nachhaltige Städte und Gemeinden", text: "Wir beraten zu Bauwerken, die langlebig, energieeffizient und auf die Bedürfnisse der Zukunft abgestimmt sind." },
      { num: 12, title: "Verantwortungsvoller Konsum und Produktion", text: "Wir arbeiten an Ressourceneffizienz, Abfallminimierung und bewusster Materialwahl in den Projekten, deren Teil wir sind." },
      { num: 13, title: "Maßnahmen zum Klimaschutz", text: "Durch ISO 14001 und Beratung zur CO₂-Reduktion tragen wir dazu bei, die Klimawirkung von Bau und Betrieb zu senken." },
      { num: 17, title: "Partnerschaften zur Erreichung der Ziele", text: "Nachhaltigkeit entsteht gemeinsam. Wir gehen enge Partnerschaften mit Kunden, Beratern und Auftragnehmern ein." },
    ],
    cta: {
      heading: "Möchten Sie mehr über unsere ESG-Arbeit erfahren?",
      body: "Wir teilen gern unsere Erfahrungen mit ISO 14001, nachhaltiger Bauherrenberatung und damit, wie wir konkret mit den UN-Nachhaltigkeitszielen in unseren Projekten arbeiten.",
      cta: "Kontaktieren Sie uns",
    },
  },
};
